import { isPlatformServer } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { WINDOW } from '@wizbii/angular-utilities';
import { combineLatest, fromEvent, Subscription } from 'rxjs';
import { debounceTime, map, startWith, throttleTime } from 'rxjs/operators';

interface ResizeData {
  searchEngineWidth: number;
  windowWidth: number;
  scrollZoneHeight: number;
}

@Component({
  selector: 'app-search-header',
  templateUrl: './search-header.component.html',
  styleUrls: ['./search-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchHeaderComponent implements AfterViewInit, OnDestroy {
  @Input() heading: string;

  @ViewChild('scrollZone') scrollZone: ElementRef;

  @ViewChild('searchEngine') searchEngine: ElementRef;

  private scrollSubscription: Subscription;

  shadowboxTransform: string;
  searchContainerFixed: boolean;

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const scrollEvent$ = fromEvent(this.window, 'scroll', { passive: true }).pipe(
      startWith(null),
      throttleTime(50, undefined, { leading: true, trailing: true })
    );

    const resizeEvent$ = fromEvent(this.window, 'resize', { passive: true });
    const resizeData$ = resizeEvent$.pipe(
      startWith(null),
      debounceTime(150),
      map(
        (): ResizeData => ({
          windowWidth: this.window.innerWidth,
          searchEngineWidth: this.searchEngine.nativeElement.getBoundingClientRect().width,
          scrollZoneHeight: this.scrollZone.nativeElement.getBoundingClientRect().height,
        })
      )
    );

    this.scrollSubscription = combineLatest([scrollEvent$, resizeData$])
      .pipe(map((res: [Event, ResizeData]): ResizeData => res[1]))
      .subscribe(this.updateSearchContainer.bind(this));
  }

  ngOnDestroy(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  private updateSearchContainer(resizeData: ResizeData): void {
    const { searchEngineWidth, windowWidth, scrollZoneHeight } = resizeData;

    const scrollZoneRatio = this.computeScrollZoneRatio(scrollZoneHeight);
    const newScaleValue = windowWidth / (searchEngineWidth + (windowWidth - searchEngineWidth) * scrollZoneRatio);

    this.window.requestAnimationFrame(() => {
      this.shadowboxTransform = `scaleX(${newScaleValue})`;
      this.searchContainerFixed = scrollZoneRatio === 0;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
  }

  /**
   * Return the inverted ratio of the window's scroll position within the scrollZone.
   * @param {number} height
   * @returns {number}
   */
  private computeScrollZoneRatio(height: number): number {
    // retrieve the position of the scrollZone in relation to the current scroll position.
    const { top } = this.scrollZone.nativeElement.getBoundingClientRect();

    // Constrain top value between 0 and scrollZone height to avoid scaling when not in scrollZone.
    const constrainedTop = Math.min(height, Math.max(0, -1 * top));

    // Compute ratio then invert it to match shadowbox scaling needs.
    return 1 - constrainedTop / height;
  }
}
